import { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import IconExpandLess from "@mui/icons-material/ExpandLess";
import IconExpandMore from "@mui/icons-material/ExpandMore";
import PeopleIcon from "@mui/icons-material/People";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import BoyIcon from "@mui/icons-material/Boy";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import FlightIcon from "@mui/icons-material/Flight";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import SevereColdIcon from "@mui/icons-material/SevereCold";
import Divider from "@mui/material/Divider";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BuildIcon from "@mui/icons-material/Build";
import CheckIcon from "@mui/icons-material/CheckBox";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ApprovalIcon from "@mui/icons-material/Approval";
import PersonIcon from "@mui/icons-material/Person";
import MenuBook from "@mui/icons-material/MenuBook";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import AlignHorizontalCenterIcon from "@mui/icons-material/AlignHorizontalCenter";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { FlightTakeoff, LocalAtm } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

import { PERMISSIONS, ROLE_CODES } from "src/constants";

import Side_Bar_Logo from "../../assets/icon/side-bar-logo.png";
import FeatureFlag from "../../components/FeatureFlag";
import useCrew from "../../hooks/useCrew";
import useFeatureFlag, { FEATURE_FLAGS } from "../../hooks/useFeatureFlag";
import useUser from "../../hooks/useUser";
import PendingCertificatesBadge from "../pending-certificates/Badge";
import { WithPermission, WithAnyPermission, WithRole, useAuthorization } from "../../components/Authorization";
import "./index.scss";

const COLOR_DEFAULT = "#526A85";

const StyledListItemIcon = styled(ListItemIcon)({
  minWidth: "2.5rem",
});

const ListItem = ({ to, children, onClick, component, setMenuOpen }) => {
  const history = useHistory();
  const isCurrentLocation = useMemo(() => history.location.pathname === to, [history.location.pathname, to]);
  return (
    <ListItemButton
      component={component}
      onClick={onClick}
      classes={{ root: `list-item${isCurrentLocation ? " active" : ""}` }}
      to={to}
    >
      {children}
    </ListItemButton>
  );
};

const SubmenuItem = ({ title, to, menuOpen, setMenuOpen, onClick }) => (
  <ListItem component={Link} to={to}>
    <ListItemText
      inset
      onClick={() => {
        onClick?.();
        setMenuOpen(false);
      }}
      slotProps={{
        primary: {
          classes: { root: "list-item-text subitem" },
        },
      }}
    >
      {title}
    </ListItemText>
  </ListItem>
);

const Submenu = ({ title, crew, icon, items, expanded = false, menuOpen, setMenuOpen, onClick }) => {
  const [isOpened, setIsOpened] = useState(expanded);
  const history = useHistory();
  const auth = useAuthorization();

  const ToggleIcon = isOpened ? IconExpandLess : IconExpandMore;

  const handleClick = () => {
    setIsOpened(!isOpened);
    onClick?.();

    const firstPermitted = items.find((item) => !item.permission || auth.hasPermission(item.permission));
    const nav = firstPermitted?.to;
    if (nav) {
      firstPermitted.onClick?.();
      history.push(nav);
    }
  };

  return (
    <>
      <ListItem onClick={handleClick} setMenuOpen={setMenuOpen}>
        <StyledListItemIcon>{icon}</StyledListItemIcon>
        <ListItemText
          slotProps={{
            primary: { classes: { root: "list-item-text" } },
          }}
        >
          {title}
        </ListItemText>
        <ToggleIcon
          onClick={(e) => {
            setIsOpened(!isOpened);
            e.stopPropagation();
          }}
        />
      </ListItem>
      <Collapse in={isOpened} timeout="auto" unmountOnExit>
        <List disablePadding>
          {items.map((item, index) => {
            const el = (
              <SubmenuItem
                key={index}
                to={item.to}
                title={item.title}
                setMenuOpen={setMenuOpen}
                onClick={item.onClick}
              />
            );
            if (item.permission) {
              return (
                <WithPermission key={index} permission={item.permission}>
                  {el}
                </WithPermission>
              );
            } else if (item.role) {
              return (
                <WithRole key={index} role={item.role}>
                  {el}
                </WithRole>
              );
            } else {
              return el;
            }
          })}
        </List>
      </Collapse>
    </>
  );
};

const UserSubmenu = ({ crew, menuOpen, setMenuOpen }) => {
  const { t } = useTranslation();

  const myCourses = t("my_courses");
  const allCourses = t("all_courses");
  const allCertificates = t("all_certificates");

  const items = [];
  if (crew.id) {
    items.push({
      title: myCourses,
      to: `/user/compliance/positions`,
    });
  }

  return (
    <Submenu
      title={t("my_courses")}
      icon={<PermContactCalendarIcon style={{ fill: COLOR_DEFAULT }} />}
      items={[
        ...items,
        {
          title: allCourses,
          to: `/user/compliance/all`,
        },
        {
          title: allCertificates,
          to: `/user/certificates`,
        },
      ]}
      setMenuOpen={setMenuOpen}
      menuOpen={menuOpen}
    />
  );
};

const UserMenu = ({ crew, menuOpen, setMenuOpen }) => {
  const { t } = useTranslation();
  const aircraftOpsEnabled = useFeatureFlag(FEATURE_FLAGS.AIRCRAFT_OPS);
  const members = t("members");

  const positionOverview = t("position_overview");
  const aircraftOverview = t("aircraft_overview");
  const crewOverview = t("crew_overview");
  const activeSubscriptions = t("active_subscriptions");

  return (
    <>
      <div className="app training">
        <Typography variant="h4">{t("sidebar.crew_overview_menu")}</Typography>
      </div>
      <UserSubmenu crew={crew} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Divider />
      <WithPermission permission={PERMISSIONS.SEE_TRAINING_DATA}>
        <Submenu
          title={t("members")}
          icon={<PeopleIcon style={{ fill: COLOR_DEFAULT }} />}
          crew={crew}
          items={[
            {
              title: members,
              to: `/crew/${crew.id}/members`,
            },
          ]}
          setMenuOpen={setMenuOpen}
          menuOpen={menuOpen}
        />
        <Divider />
      </WithPermission>

      {/* Ignore Crew ID #150 because it is the Vertical Crew Pool (individual users - not an actual crew) */}
      {crew.id !== 150 && (
        <WithPermission permission={PERMISSIONS.SEE_TRAINING_DATA}>
          <Submenu
            title={t("members_properties")}
            icon={<PeopleIcon style={{ fill: COLOR_DEFAULT }} />}
            crew={crew}
            items={[
              ...(aircraftOpsEnabled
                ? [
                    {
                      title: aircraftOverview,
                      to: `/crew/${crew.id}/aircraft-overview`,
                    },
                  ]
                : []),
              {
                title: positionOverview,
                to: `/crew/${crew.id}/position-overview`,
              },
              {
                title: crewOverview,
                to: `/crew/${crew.id}/overview`,
              },
              {
                title: activeSubscriptions,
                to: `/crew/${crew.id}/subscriptions`,
                permission: PERMISSIONS.BUY_COURSE_FOR_CREW_MEMBERS,
              },
            ]}
            setMenuOpen={setMenuOpen}
            menuOpen={menuOpen}
          />
          <Divider />
        </WithPermission>
      )}

      <WithAnyPermission
        permissions={[
          PERMISSIONS.CONFIGURE_COMPANY_CUSTOM_COURSES,
          crew.moodleCompanyCourses ? PERMISSIONS.CONFIGURE_COMPANY_LMS_COURSE : null,
        ].filter(Boolean)}
      >
        <>
          <Submenu
            title={t("sidebar.company_custom_courses")}
            icon={<MenuBook style={{ fill: COLOR_DEFAULT }} />}
            items={[
              {
                title: t("sidebar.custom_certificate"),
                to: `/crew/${crew.id}/customCourses`,
                permission: PERMISSIONS.CONFIGURE_COMPANY_CUSTOM_COURSES,
              },
              {
                title: t("sidebar.add_custom_certificate"),
                to: `/crew/${crew.id}/customCourses/add`,
                permission: PERMISSIONS.CONFIGURE_COMPANY_CUSTOM_COURSES,
              },
              ...(crew.moodleCompanyCourses
                ? [
                    {
                      title: t("sidebar.company_lms_course"),
                      to: `/crew/${crew.id}/companyCourses`,
                      permission: PERMISSIONS.CONFIGURE_COMPANY_LMS_COURSE,
                    },
                    {
                      title: t("sidebar.add_lms_company_course"),
                      to: `/crew/${crew.id}/companyCourses/add`,
                      permission: PERMISSIONS.CONFIGURE_COMPANY_LMS_COURSE,
                    },
                  ]
                : []),
            ]}
            setMenuOpen={setMenuOpen}
            menuOpen={menuOpen}
          />
          <Divider />
        </>
      </WithAnyPermission>

      <WithPermission permission={PERMISSIONS.CONFIGURE_AIRCRAFT}>
        <ListItem component={Link} to={`/crew/${crew.id}/aircraft`}>
          <StyledListItemIcon>
            <FlightIcon style={{ fill: COLOR_DEFAULT }} />
          </StyledListItemIcon>
          <ListItemText
            onClick={() => setMenuOpen(false)}
            slotProps={{
              primary: { classes: { root: "list-item-text" } },
            }}
          >
            {t("sidebar.company_aircraft")}
          </ListItemText>
        </ListItem>
        <Divider />
      </WithPermission>

      <FeatureFlag flag={FEATURE_FLAGS.AIRCRAFT_OPS}>
        <WithPermission permission={PERMISSIONS.MANAGE_AIRCRAFT_OPERATIONS}>
          <ListItem component={Link} to={`/crew/${crew.id}/limitations`}>
            <StyledListItemIcon>
              <SevereColdIcon style={{ fill: COLOR_DEFAULT }} />
            </StyledListItemIcon>
            <ListItemText
              onClick={() => setMenuOpen(false)}
              slotProps={{
                primary: { classes: { root: "list-item-text" } },
              }}
            >
              {t("limitations.title")}
            </ListItemText>
          </ListItem>
          <Divider />
        </WithPermission>
      </FeatureFlag>

      <WithPermission permission={PERMISSIONS.CONFIGURE_COURSES}>
        <ListItem component={Link} to={`/crew/${crew.id}/relations`} onClick={() => setMenuOpen(false)}>
          <StyledListItemIcon>
            <AccountTreeIcon style={{ fill: COLOR_DEFAULT }} />
          </StyledListItemIcon>
          <ListItemText
            slotProps={{
              primary: { classes: { root: "list-item-text" } },
            }}
          >
            {t("sidebar.relations_tree")}
          </ListItemText>
        </ListItem>
        <Divider />
      </WithPermission>

      <WithPermission permission={PERMISSIONS.APPROVE_CERTIFICATES}>
        <ListItem component={Link} to={`/crew/${crew.id}/pending`}>
          <StyledListItemIcon>
            <CheckIcon style={{ fill: COLOR_DEFAULT }} />
          </StyledListItemIcon>
          <ListItemText
            slotProps={{
              primary: { classes: { root: "list-item-text" } },
            }}
          >
            {t("sidebar.approve_courses")} <PendingCertificatesBadge />
          </ListItemText>
        </ListItem>
        <Divider />
      </WithPermission>

      <WithPermission permission={PERMISSIONS.BULK_UPLOAD_CERTIFICATES}>
        <Submenu
          title={t("certificate_upload.bulk.menu_item")}
          icon={<UploadFileIcon style={{ fill: COLOR_DEFAULT }} />}
          items={[
            {
              title: t("certificate_upload.bulk.multi_user_menu_item"),
              to: `/crew/${crew.id}/bulk-upload`,
              permission: PERMISSIONS.BULK_UPLOAD_CERTIFICATES,
            },
            {
              title: t("certificate_upload.bulk.single_user.menu_item"),
              to: `/crew/${crew.id}/bulk-upload/user-courses`,
              permission: PERMISSIONS.BULK_UPLOAD_CERTIFICATES,
            },
          ]}
          setMenuOpen={setMenuOpen}
          menuOpen={menuOpen}
        />
        <Divider />
      </WithPermission>
      <WithPermission permission={PERMISSIONS.CONFIGURE_ROLES}>
        <ListItem component={Link} to={`/crew/${crew.id}/roles`}>
          <StyledListItemIcon>
            <AccountCircleIcon style={{ fill: COLOR_DEFAULT }} />
          </StyledListItemIcon>
          <ListItemText
            slotProps={{
              primary: { classes: { root: "list-item-text" } },
            }}
          >
            {t("roles_and_permissions")}
          </ListItemText>
        </ListItem>
        <Divider />
      </WithPermission>

      <WithPermission permission={PERMISSIONS.VIEW_ORDERS}>
        <ListItem component={Link} to={"/orders-crew"}>
          <StyledListItemIcon>
            <LocalAtm style={{ fill: COLOR_DEFAULT }} />
          </StyledListItemIcon>
          <ListItemText
            slotProps={{
              primary: { classes: { root: "list-item-text" } },
            }}
          >
            {t("orders_crew")}
          </ListItemText>
        </ListItem>
        <Divider />
      </WithPermission>
    </>
  );
};

const SuperAdminTrainingMenu = ({ menuOpen, setMenuOpen }) => {
  const { t } = useTranslation();

  return (
    <List>
      <Submenu
        title={t("sidebar.define_testing")}
        icon={<AlignHorizontalCenterIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.observable_items"),
            to: "/observable",
          },
          {
            title: t("sidebar.activities_training"),
            to: "/activities-training",
          },
          {
            title: t("sidebar.process_step"),
            to: "/process-step",
          },

          {
            title: t("sidebar.associate_position_process_step"),
            to: "/associate-position-process-step",
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />

      <Submenu
        title={t("sidebar.plan_training")}
        icon={<SchoolIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.all_trainings_add_training"),
            to: "/trainings",
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />

      <Submenu
        title={t("sidebar.manage_trainings")}
        icon={<ManageHistoryIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.trainings_list"),
            to: "/system-trainer-trainings",
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />
    </List>
  );
};

const UserTrainingMenu = () => {
  const { t } = useTranslation();

  return (
    <>
      <ListItem component={Link} to={"/user-trainings"}>
        <StyledListItemIcon>
          <BoyIcon style={{ fill: COLOR_DEFAULT }} />
        </StyledListItemIcon>
        <ListItemText
          slotProps={{
            primary: { classes: { root: "list-item-text" } },
          }}
        >
          {t("sidebar.my_trainings")}
        </ListItemText>
      </ListItem>
      <Divider />
    </>
  );
};

const CrewAdminTrainingMenu = ({ crew, menuOpen, setMenuOpen }) => {
  const { t } = useTranslation();

  return (
    <>
      <Submenu
        title={t("sidebar.crew_trainings")}
        icon={<AdminPanelSettingsIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.member_trainings"),
            to: `/crew-users-trainings/${crew.id}`,
          },
          {
            title: t("sidebar.available_trainings"),
            to: `/crew-trainings/${crew.id}`,
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />
    </>
  );
};

const TrainerTrainingMenu = () => {
  const { t } = useTranslation();

  return (
    <>
      <ListItem component={Link} to={"/trainer-trainings"}>
        <StyledListItemIcon>
          <CoPresentIcon style={{ fill: COLOR_DEFAULT }} />
        </StyledListItemIcon>
        <ListItemText
          slotProps={{
            primary: { classes: { root: "list-item-text" } },
          }}
        >
          {t("sidebar.trainer_classroom")}
        </ListItemText>
      </ListItem>
      <Divider />
    </>
  );
};

const InstructorTrainingMenu = () => {
  const { t } = useTranslation();

  return (
    <>
      <ListItem component={Link} to={"/instructor-trainings"}>
        <StyledListItemIcon>
          <PersonSearchIcon style={{ fill: COLOR_DEFAULT }} />
        </StyledListItemIcon>
        <ListItemText
          slotProps={{
            primary: { classes: { root: "list-item-text" } },
          }}
        >
          {t("sidebar.instructor_practical")}
        </ListItemText>
      </ListItem>

      <Divider />
    </>
  );
};

const AssessorTrainingMenu = () => {
  const { t } = useTranslation();

  return (
    <>
      <ListItem component={Link} to={"/assessor-trainings"}>
        <StyledListItemIcon>
          <PersonPinCircleIcon style={{ fill: COLOR_DEFAULT }} />
        </StyledListItemIcon>
        <ListItemText
          slotProps={{
            primary: { classes: { root: "list-item-text" } },
          }}
        >
          {t("sidebar.assessor")}
        </ListItemText>
      </ListItem>
      <Divider />
    </>
  );
};

const CheckerTrainingMenu = () => {
  const { t } = useTranslation();

  return (
    <>
      <ListItem component={Link} to={"/checker-trainings"}>
        <StyledListItemIcon>
          <ApprovalIcon style={{ fill: COLOR_DEFAULT }} />
        </StyledListItemIcon>
        <ListItemText
          slotProps={{
            primary: { classes: { root: "list-item-text" } },
          }}
        >
          {t("sidebar.checker")}
        </ListItemText>
      </ListItem>
      <Divider />
    </>
  );
};

const SuperAdminMenu = ({ menuOpen, setMenuOpen }) => {
  const { t } = useTranslation();
  return (
    <List>
      <Submenu
        title={t("sidebar.courses")}
        icon={<SchoolIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.all_moodle_courses"),
            to: "/courses/moodle",
          },
          {
            title: t("sidebar.all_custom_certificates"),
            to: "/courses/custom",
          },
          {
            title: t("sidebar.add_custom_certificate"),
            to: "/courses/custom/add",
          },
          {
            title: t("sidebar.course_bundles"),
            to: "/bundles",
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />

      <Submenu
        title={t("sidebar.courses_groups")}
        icon={<AllInboxIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.groups"),
            to: "/courses_groups",
          },
          {
            title: t("sidebar.add_group"),
            to: "/courses_groups/add_courses_group",
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />
      <Submenu
        title={t("sidebar.users")}
        icon={<PersonIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          { title: t("sidebar.all_users"), to: `/users` },
          { title: t("sidebar.subscriptions"), to: `/subscriptions` },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />
      <Submenu
        title={t("sidebar.companies")}
        icon={<PeopleIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[{ title: t("sidebar.all_companies"), to: `/crews` }]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />
      <Submenu
        title={t("sidebar.positions")}
        icon={<WorkIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.positions"),
            to: "/positions",
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />
      <Submenu
        title={t("sidebar.operations")}
        icon={<BuildIcon style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.operations"),
            to: "/operations",
          },
          {
            title: t("sidebar.add_operation"),
            to: "/operations/add_operation",
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />
      <ListItem component={Link} to={"/relations"}>
        <StyledListItemIcon>
          <AccountTreeIcon style={{ fill: COLOR_DEFAULT }} />
        </StyledListItemIcon>
        <ListItemText
          onClick={() => setMenuOpen(false)}
          slotProps={{
            primary: { classes: { root: "list-item-text" } },
          }}
        >
          {t("sidebar.relations_tree")}
        </ListItemText>
      </ListItem>
      <Divider />
      <ListItem component={Link} to={"/roles"}>
        <StyledListItemIcon>
          <AccountCircleIcon style={{ fill: COLOR_DEFAULT }} />
        </StyledListItemIcon>
        <ListItemText
          slotProps={{
            primary: { classes: { root: "list-item-text" } },
          }}
        >
          {t("roles_and_permissions")}
        </ListItemText>
      </ListItem>
      <Divider />
      <Submenu
        title={t("sidebar.orders")}
        icon={<LocalAtm style={{ fill: COLOR_DEFAULT }} />}
        items={[
          {
            title: t("sidebar.orders"),
            to: "/orders",
          },
          {
            title: t("sidebar.transactions_bank"),
            to: "/transactions-bank",
          },
          {
            title: t("sidebar.upload_bank_file"),
            to: "/upload-bank-file",
          },
        ]}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
      <Divider />
    </List>
  );
};

// new sidebar to use delete the old sidebar above
const Sidebar = ({ menuOpen, setMenuOpen }) => {
  const { t } = useTranslation();
  const { crew } = useCrew();
  const user = useUser();

  return (
    <div className="side-bar" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="logo-wrapper" style={{ flex: "0 0 auto" }}>
        <div>
          <Link to="/" onClick={() => setMenuOpen(false)}>
            <img src={Side_Bar_Logo} alt={t("sidebar.logo_alt")} />
          </Link>
        </div>
        <div className="close-icon" onClick={() => setMenuOpen(false)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.16998 14.83L14.83 9.17004"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.83 14.83L9.16998 9.17004"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      <hr className="menu-divider" />

      <div style={{ flex: "1 1 auto", overflow: "auto", minHeight: 0 }}>
        {crew.id !== 0 ? (
          <UserMenu crew={crew} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        ) : (
          <SuperAdminMenu crew={crew} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        )}

        {(crew.id == 0 || user.ta) && (
          <div className="app training">
            <Typography variant="h4">{t("sidebar.admin_training")}</Typography>
          </div>
        )}
        {(crew.id == 0 || user.ta) && (
          <SuperAdminTrainingMenu crew={crew} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        )}

        {crew.id !== 0 && (
          <>
            <div>
              <Typography variant="h4">{t("sidebar.dg_crm_crew_training")}</Typography>
            </div>
            <UserTrainingMenu />

            {/* TODO: remove this entirely if none missing it
            <WithAnyRole
              roles={[
                COMPUTED_ROLES.CREW_ADMIN,
                COMPUTED_ROLES.SUPER_ADMIN,
                ROLE_CODES.NOMINATED_PERSON_CREW_TRAINING,
                ROLE_CODES.NOMINATED_PERSON_GROUND_OPERATION,
              ]}
            >
              <CrewAdminTrainingMenu crew={crew} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
            </WithAnyRole> */}

            <WithRole role={ROLE_CODES.TRAINER}>
              <TrainerTrainingMenu />
            </WithRole>

            <WithRole role={ROLE_CODES.INSTRUCTOR}>
              <InstructorTrainingMenu />
            </WithRole>

            <WithRole role={ROLE_CODES.ASSESSOR}>
              <AssessorTrainingMenu />
            </WithRole>

            <WithRole role={ROLE_CODES.CHECKER}>
              <CheckerTrainingMenu />
            </WithRole>
          </>
        )}
      </div>

      <div className="app links" style={{ flex: "0 0 auto" }}>
        <ListItem onClick={() => window.open(process.env.REACT_APP_DASHBOARD_URI, "_blank")}>
          <StyledListItemIcon>
            <FlightTakeoff style={{ fill: COLOR_DEFAULT }} />
          </StyledListItemIcon>
          <ListItemText
            onClick={() => setMenuOpen(false)}
            slotProps={{
              primary: { classes: { root: "list-item-text" } },
            }}
          >
            {t("sidebar.cockpit")}
          </ListItemText>
        </ListItem>
      </div>
    </div>
  );
};

export default Sidebar;
